import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Translate from "../../utils/Translate";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../utils/Api";
import Preloader from "../Preloader";
import { getCurrentDateMask, getCurrentInputFormat } from "../../utils/dateTimeFormat";
import { DatePicker } from "@mui/x-date-pickers";
import { accessKeys, hasSomeAccess } from "../../utils/userAccess";

export default function StUnitForm({ stUnit: stUnitProp, onClose, onSave }) {
  const [stUnit, setStUnit] = useState(stUnitProp);
  const [areas, setAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const api = useApi();

  useEffect(() => {
    async function init() {
      setIsLoading(true);
      const areas = await loadAreas(api);
      setAreas(areas ?? []);
      setIsLoading(false);
    }
    init();
  }, [api]);

  const handleUpdateStUnitValue = useCallback((name, value) => setStUnit((prev) => ({ ...prev, [name]: value })), []);

  const handleSaveStUnit = useCallback(async () => {
    const qty = stUnit.isUnlimitedQty ? Infinity : Number(stUnit.qty);

    var response = null;
    if (qty === 0) {
      response = await deleteStUnit(stUnit.stUnitId, api);
    } else {
      response = await saveStUnit(stUnit, api);
    }

    if (response && response.isSuccessful) {
      await onSave();
    }
  }, [api, stUnit, onClose, onSave]);

  return (
    <>
      {isLoading && <Preloader />}
      <DialogTitle>{stUnit.stUnitId ? Translate.get("EditStUnitLabel") : Translate.get("AddStUnitLabel")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={5} paddingTop={1}>
          <Grid container spacing={2} size={12}>
            <Grid size={12}>
              <InnerSelectFromList
                required
                labelKey="StUnitLocation"
                list={areas}
                itemLabelName="shortName"
                itemValueName="locationId"
                value={stUnit.locationId}
                handleUpdateValue={(newValue) => handleUpdateStUnitValue("locationId", newValue)}
              />
            </Grid>

            <Grid size={12}>
              <InnerTextField
                labelKey="PricePerUnit"
                value={stUnit.pcsPrice}
                handleUpdateValue={(newValue) => handleUpdateStUnitValue("pcsPrice", newValue)}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  type: "number",
                  min: 0,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">SEK</InputAdornment>,
                }}
              />
            </Grid>

            <Grid size={12}>
              <InnerTextField
                required
                labelKey="Quantity"
                value={stUnit.isUnlimitedQty ? "∞" : stUnit.qty}
                handleUpdateValue={(newValue) => handleUpdateStUnitValue("qty", newValue)}
                disabled={stUnit.isUnlimitedQty}
                inputProps={
                  stUnit.isUnlimitedQty
                    ? {}
                    : {
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        type: "number",
                        min: 0,
                      }
                }
                helperText={
                  stUnit.isUnlimitedQty || !stUnitProp.stUnitId
                    ? null
                    : stUnitProp.qty < stUnit.qty
                      ? `${Translate.get("Increasing")} ${stUnit.qty - stUnitProp.qty}`
                      : stUnitProp.qty > stUnit.qty
                        ? `${Translate.get("Decreasing")} ${stUnitProp.qty - stUnit.qty}`
                        : null
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} size={12}>
            <Grid size={12}>
              <InnerTextField
                labelKey="StUnitRevisionLabel"
                inputProps={{ maxLength: 50 }}
                value={stUnit.revision}
                handleUpdateValue={(newValue) => handleUpdateStUnitValue("revision", newValue)}
              />
            </Grid>
            <Grid size={12}>
              <InnerTextField
                labelKey="StUnitPrintingLabel"
                inputProps={{ maxLength: 50 }}
                value={stUnit.printing}
                handleUpdateValue={(newValue) => handleUpdateStUnitValue("printing", newValue)}
              />
            </Grid>
            <Grid size={12}>
              <DatePicker
                displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                format={getCurrentInputFormat()}
                mask={getCurrentDateMask()}
                label={Translate.get("StUnitBestBeforeDate")}
                value={stUnit.bestBeforeDate ? new Date(stUnit.bestBeforeDate) : null}
                onChange={(newValue) => handleUpdateStUnitValue("bestBeforeDate", newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          </Grid>

          <Grid marginLeft={0.3} spacing={1} size={12}>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stUnit.isUnlimitedQty}
                    onChange={(event) => handleUpdateStUnitValue("isUnlimitedQty", event.target.checked)}
                  />
                }
                label={Translate.get("IsUnlimitedQty")}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stUnit.blocked}
                    onChange={(event) => handleUpdateStUnitValue("blocked", event.target.checked)}
                  />
                }
                label={Translate.get("StUnitStatusBlocked")}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{Translate.get("Cancel")}</Button>
        <ButtonWithSpinner
          variant="contained"
          onClick={handleSaveStUnit}
          disabled={!stUnit.locationId || (stUnit.isUnlimitedQty ? false : !stUnit.qty || stUnit.qty < 0)}
        >
          {Translate.get("Save")}
        </ButtonWithSpinner>
      </DialogActions>
    </>
  );
}

function InnerTextField({
  labelKey,
  inputProps,
  InputProps,
  value,
  handleUpdateValue,
  helperText,
  disabled,
  required,
}) {
  return (
    <TextField
      fullWidth
      required={required}
      disabled={disabled}
      helperText={helperText}
      value={value ?? ""}
      InputProps={{ ...InputProps, spellCheck: false }}
      onChange={(event) => handleUpdateValue(event.target.value)}
      label={Translate.get(labelKey)}
      inputProps={inputProps}
    />
  );
}

function InnerSelect({ labelKey, items, value, handleUpdateValue, disabledFn, hideFn, required }) {
  let sortedItems = items ? [...items].sort((a, b) => a.label.localeCompare(b.label)) : items;
  return (
    <FormControl fullWidth required={required}>
      <InputLabel id={labelKey + "id"}>{Translate.get(labelKey)}</InputLabel>
      <Select
        labelId={labelKey + "id"}
        id={labelKey + "select"}
        value={value ?? ""}
        label={Translate.get(labelKey)}
        onChange={(event) => handleUpdateValue(event.target.value)}
      >
        {sortedItems &&
          sortedItems
            .filter((item) => (hideFn ? !hideFn(item.value) : true))
            .map((item, index) => (
              <MenuItem key={index} value={item.value} disabled={disabledFn ? disabledFn(item.value) : false}>
                {item.label}
              </MenuItem>
            ))}
      </Select>
    </FormControl>
  );
}

function InnerSelectFromList({
  labelKey,
  list,
  itemLabelName,
  itemValueName,
  value,
  handleUpdateValue,
  compareFn,
  required,
}) {
  const items = list.map((item) => ({ label: item[itemLabelName], value: item[itemValueName] }));
  return (
    <InnerSelect
      required={required}
      labelKey={labelKey}
      items={items}
      value={value}
      handleUpdateValue={handleUpdateValue}
      compareFn={compareFn}
    />
  );
}

async function loadAreas(api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/areas`;
  const response = await api.fetch(url, false, "GET");
  if (response.isSuccessful) {
    return response.value;
  } else {
    return null;
  }
}

async function saveStUnit(stUnit, api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/stunits`;
  const response = await api.fetch(url, stUnit, "POST");
  return response;
}

async function deleteStUnit(stUnitId, api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/stunits/${stUnitId}`;
  const response = await api.fetch(url, {}, "DELETE");
  return response;
}
